/**
 * URL ↔ shell-state mapping — the ONE place the route grammar lives.
 *
 * The grammar IS the Entity Path Pattern (platform-architecture.md): the same
 * hierarchical paths the AI tools, subscriptions, and storage keys use are the
 * browser URLs, so a copied link names the entity it shows:
 *
 *   /                                     home (org-wide)
 *   /flows                                the flows canvas (org-wide)
 *   /findings                             the findings log (org-wide)
 *   /gateways/<gw>                        gateway detail
 *   /gateways/<gw>/controllers/<cid>      controller detail (symbol grid)
 *   /gateways/<gw>/cameras/<cid>          camera detail (live feed)
 *   /gateways/<gw>/models/<mid>           model detail
 *   /gateways/<gw>/integrations/<id>      integration detail (schema tree)
 *   /gateways/<gw>/dashboards/<id>        dashboard detail (tile canvas)
 *
 * Pure functions only — history wiring (pushState/popstate) stays in the shell,
 * which remains the single owner of view state. Unknown/malformed paths parse
 * to home: a stale or hand-typed URL degrades to the landing page, never a
 * broken view.
 *
 * A controller page also takes options after the path — see `parseControllerOpen`.
 */

export type View =
  | 'home' | 'canvas' | 'findings' | 'gateway' | 'controller' | 'camera' | 'model' | 'integration' | 'dashboard';

export interface Route {
  view: View;
  gatewayId?: string;
  controllerId?: string;
  cameraId?: string;
  modelId?: string;
  integrationId?: string;
  dashboardId?: string;
}

/** Parse a pathname into a route. Tolerant: anything unrecognized → home. */
export function parseRoute(pathname: string): Route {
  const seg = pathname.split('/').filter(Boolean).map(decodeURIComponent);
  if (seg.length === 0) return { view: 'home' };
  if (seg[0] === 'flows' && seg.length === 1) return { view: 'canvas' };
  if (seg[0] === 'findings' && seg.length === 1) return { view: 'findings' };
  if (seg[0] !== 'gateways' || !seg[1]) return { view: 'home' };
  const gatewayId = seg[1];
  if (seg.length === 2) return { view: 'gateway', gatewayId };
  if (seg[2] === 'controllers' && seg[3] && seg.length === 4) {
    return { view: 'controller', gatewayId, controllerId: seg[3] };
  }
  if (seg[2] === 'cameras' && seg[3] && seg.length === 4) {
    return { view: 'camera', gatewayId, cameraId: seg[3] };
  }
  if (seg[2] === 'models' && seg[3] && seg.length === 4) {
    return { view: 'model', gatewayId, modelId: seg[3] };
  }
  if (seg[2] === 'integrations' && seg[3] && seg.length === 4) {
    return { view: 'integration', gatewayId, integrationId: seg[3] };
  }
  if (seg[2] === 'dashboards' && seg[3] && seg.length === 4) {
    return { view: 'dashboard', gatewayId, dashboardId: seg[3] };
  }
  return { view: 'gateway', gatewayId }; // deeper/unknown sub-path → the gateway page
}

/** Format a route into its canonical pathname. Missing required ids → '/'. */
export function routePath(r: Route): string {
  const e = encodeURIComponent;
  switch (r.view) {
    case 'canvas':
      return '/flows';
    case 'findings':
      return '/findings';
    case 'gateway':
      return r.gatewayId ? `/gateways/${e(r.gatewayId)}` : '/';
    case 'controller':
      return r.gatewayId && r.controllerId ? `/gateways/${e(r.gatewayId)}/controllers/${e(r.controllerId)}` : '/';
    case 'camera':
      return r.gatewayId && r.cameraId ? `/gateways/${e(r.gatewayId)}/cameras/${e(r.cameraId)}` : '/';
    case 'model':
      return r.gatewayId && r.modelId ? `/gateways/${e(r.gatewayId)}/models/${e(r.modelId)}` : '/';
    case 'integration':
      return r.gatewayId && r.integrationId
        ? `/gateways/${e(r.gatewayId)}/integrations/${e(r.integrationId)}`
        : '/';
    case 'dashboard':
      return r.gatewayId && r.dashboardId
        ? `/gateways/${e(r.gatewayId)}/dashboards/${e(r.dashboardId)}`
        : '/';
    default:
      return '/';
  }
}

/** The controller page's tabs, as its options name them. */
export const CONTROLLER_TABS = ['symbols', 'history', 'properties', 'program', 'logs', 'findings'] as const;
export type ControllerTab = (typeof CONTROLLER_TABS)[number];

/** How many symbols the History tab stacks as lanes (MAX_HISTORY_SYMBOLS there). */
const OPEN_SYMBOLS_MAX = 3;

/** What a controller page is asked to open with: `?tab=history&symbol=A&symbol=B&minutes=60`. */
export interface ControllerOpen {
  tab: ControllerTab;
  /** History lanes, in order — the paths exactly as `symbols.search` returns them. */
  symbols: string[];
  /** The History window: the last N minutes, ending now. */
  minutes?: number;
}

/**
 * THE OPTIONS A CONTROLLER PAGE IS OPENED WITH, read from the query string — or nothing.
 *
 * The path names the entity; these say how to show it on arrival: which tab, which symbols in the
 * History lanes, how far back. They are an ARRIVAL, not part of the address: the shell applies them
 * once and puts the bare path back in the address bar, because a person changes lanes and tabs after
 * arriving, and a reload that re-applied the arrival would throw their changes away.
 *
 * Symbols or minutes without a tab mean History, the only tab that reads them — so an assistant that
 * names the symbols and forgets the tab still lands where it meant to. Anything unrecognised is
 * dropped rather than refused: the page itself is still the right one to show.
 */
export function parseControllerOpen(search: string): ControllerOpen | undefined {
  const q = new URLSearchParams(search);
  const symbols: string[] = [];
  for (const raw of q.getAll('symbol')) {
    const s = raw.trim();
    if (s && !symbols.includes(s) && symbols.length < OPEN_SYMBOLS_MAX) symbols.push(s);
  }
  const m = Number(q.get('minutes'));
  const minutes = Number.isFinite(m) && m > 0 ? m : undefined;
  const named = q.get('tab')?.trim().toLowerCase();
  const tab = CONTROLLER_TABS.find((x) => x === named) ?? (symbols.length || minutes ? 'history' : undefined);
  if (!tab) return undefined;
  return { tab, symbols, ...(minutes ? { minutes } : {}) };
}
